export type MembershipRenewalReason = 'DATE' | 'VISITS';

/**
 * Decision 1 / Validated behavior (membership-renewal plan, 2026-09-24) — a
 * cycle ends by `endDate` OR when its visits are used up, whichever first.
 * `status` is a plain string (the `UserMembershipStatus` enum lives in the
 * backend entity); only ACTIVE and FROZEN rows can be up for renewal at all.
 * `endDate` is the exclusive expiry day, same convention as
 * `membershipCoversDay`. A freeze active ON `today` suppresses renewal
 * outright — the client cannot book or check in to spend the visit that
 * would trigger VISITS, and a DATE renewal would fire mid-freeze, which the
 * staff flow never intends.
 */
export interface MembershipRenewalStateRow {
  /** The row's own duration snapshot (Foundation 2), never the package's
   *  live value — a one-day ticket is never up for renewal. */
  duration: number;
  durationUnit: string;
  status: string;
  endDate: string;
  numberOfWorkouts: number;
  sessionsUsed: number;
  /** Non-null once `endCycleOn` has moved this row's `endDate` early. */
  endedEarlyAt?: Date | null | string;
  /** Current freeze window `[freezeStartDate, freezeEndDate)`, if any. */
  freezeStartDate?: null | string;
  freezeEndDate?: null | string;
}

/**
 * Gates that are not on the row's own date/count fields but still decide
 * eligibility: a direct live successor (Ruling A, amended by Ruling Z —
 * another row replacing this one, not CANCELED/CANCELED_REFUND/ARCHIVED/
 * EXPIRED), and any linked card
 * Subscription in any status (a canceled card never silently becomes
 * cardless auto-renewal).
 *
 * Ruling E (fix round 1) — a scheduled first-charge date is deliberately NOT
 * a gate here. This projection is informational ("up for renewal"), and a
 * row with a scheduled activation is still genuinely up for renewal; the
 * plan's scheduled-activation exclusion applies only to AUTOMATIC renewal
 * eligibility, which a later task implements as its own, separate check
 * before firing a renewal — never by hiding the state from staff.
 */
export interface MembershipRenewalFlags {
  hasSuccessor: boolean;
  cardManaged: boolean;
}

const RENEWABLE_STATUSES = new Set(['ACTIVE', 'FROZEN']);

/**
 * Live test 1 / Ruling AC (O4) — a one-day ticket (`duration` 1,
 * `durationUnit` DAYS, the package the used-up dialog sells as "Dnevna
 * karta") covers one day and is bought again, never renewed.
 */
export function isOneDayMembership(row: {
  duration: number;
  durationUnit: string;
}) {
  return row.duration === 1 && row.durationUnit === 'DAYS';
}

/**
 * Pure projection: is `row` up for renewal on `today` (organization civil
 * day), and why. VISITS wins strictly before `endDate` (the visits ran out
 * first); DATE takes over once `today` reaches `endDate` — "whichever first"
 * (Decision 1). `null` covers every non-eligible case: wrong status, a
 * one-day ticket, an early end already recorded, a live freeze on `today`, a successor already
 * assigned, or a card managing the row.
 */
export function membershipRenewalState(
  row: MembershipRenewalStateRow,
  today: string,
  flags: MembershipRenewalFlags,
): { reason: MembershipRenewalReason } | null {
  if (!RENEWABLE_STATUSES.has(row.status)) return null;
  if (isOneDayMembership(row)) return null;
  if (row.endedEarlyAt) return null;
  if (flags.hasSuccessor || flags.cardManaged) return null;
  const { freezeEndDate, freezeStartDate } = row;
  const frozenToday =
    !!freezeStartDate &&
    freezeStartDate <= today &&
    (!freezeEndDate || today < freezeEndDate);
  if (frozenToday) return null;

  if (row.sessionsUsed >= row.numberOfWorkouts && today < row.endDate) {
    return { reason: 'VISITS' };
  }
  if (row.endDate <= today) {
    return { reason: 'DATE' };
  }
  return null;
}

/**
 * Task 3 (membership-renewal plan) — Ruling B: who started a renewal cycle,
 * stored on the NEW row (`UserMemberships.renewalOrigin`). 'STAFF' is
 * "Obnovi istu"; 'AUTO_DATE' the date sweep; 'AUTO_VISITS' used-up visits on
 * a client's own booking. An ordinary assignment or Replace carries none.
 */
export type MembershipRenewalOrigin = 'STAFF' | 'AUTO_DATE' | 'AUTO_VISITS';

/**
 * Task 8 (membership-renewal plan) — whether a cycle is paid. `null` is not a
 * state: it means "not a manual cycle" (a card settles a card-managed row,
 * and its charges are not manual ledger entries, so nothing here can judge
 * it). `UNKNOWN` means the server could not state the money exactly — a
 * payment with no ledger record, money kept in another currency, or a price
 * with no whole-minor-unit value — and is never guessed into PAID/UNPAID.
 */
export type MembershipPaymentStateName =
  | 'PAID'
  | 'PARTIAL'
  | 'UNPAID'
  | 'UNKNOWN';

export interface MembershipPaymentStateInput {
  /** `finalPrice` in minor units of the ASSIGNMENT currency; `null` when it
   *  has no exact minor-unit value. */
  finalPriceMinor: bigint | null;
  /** What is still kept on the row's ledger charges (refunds and voids
   *  netted), in the assignment currency. Only meaningful when `covered`. */
  netCollectedMinor: bigint;
  /** Σ of the row's append-only discounts (assignment currency). */
  discountsMinor: bigint;
  cardManaged: boolean;
  /** Every live payment of the row has a ledger record and all money kept
   *  on it is in the assignment currency. */
  covered: boolean;
}

// see `money.ts`: no `n` literals under the frontend's ES6 target
const NO_MINOR = BigInt(0);

/**
 * `dueMinor = max(0, price − discounts − netCollected)`, only when that is
 * exact. PAID at zero due (a free cycle, or one a discount fully covers, is
 * settled). While something is still due: PARTIAL only once money was
 * actually collected (Live test 1 / Ruling AC, O2 — a discount lowers the
 * due but collects nothing, so a discount-only cycle is still UNPAID).
 */
export function membershipPaymentState(input: MembershipPaymentStateInput): {
  state: MembershipPaymentStateName | null;
  dueMinor: bigint | null;
} {
  if (input.cardManaged) return { dueMinor: null, state: null };
  if (!input.covered || input.finalPriceMinor === null) {
    return { dueMinor: null, state: 'UNKNOWN' };
  }
  const rest =
    input.finalPriceMinor - input.netCollectedMinor - input.discountsMinor;
  const dueMinor = rest > NO_MINOR ? rest : NO_MINOR;
  if (dueMinor === NO_MINOR) return { dueMinor, state: 'PAID' };
  return {
    dueMinor,
    state: input.netCollectedMinor > NO_MINOR ? 'PARTIAL' : 'UNPAID',
  };
}
